import { useState } from 'react'
import { motion } from 'framer-motion'
import SectionHeading from '../../components/SectionHeading'


interface FAQData {
  id: string
  question: string
  answer: string
  column: 'left' | 'right'
}

const PRIMARY_FAQS: FAQData[] = [
  {
    id: 'faq-1',
    question: 'Do you provide airport transfers?',
    answer:
      'Yes, we provide 24/7 private transfers to and from Salzburg Airport (SZG), Munich Airport (MUC), Vienna (VIE), and Innsbruck (INN). Your driver meets you inside arrivals with a personalized name sign.',
    column: 'left',
  },
  {
    id: 'faq-2',
    question: 'Do you track flight delays?',
    answer:
      'Yes, we monitor your flight status in real time using your flight number. If your flight is delayed or arrives early, your pickup time is automatically adjusted at no extra charge.',
    column: 'left',
  },
  {
    id: 'faq-3',
    question: 'Can I book a private day trip?',
    answer:
      "Yes! We offer fully private day trips and customized tours to top destinations like Hallstatt, Eagle's Nest (Kehlsteinhaus), the Austrian Lake District (Salzkammergut), and Grossglockner.",
    column: 'left',
  },
  {
    id: 'faq-4',
    question: 'Is hotel pickup included?',
    answer:
      'Yes, door-to-door service is included with every booking. We pick you up and drop you off directly at your hotel, private address, or designated meeting point in Salzburg and surrounding areas.',
    column: 'right',
  },
  {
    id: 'faq-5',
    question: 'Can I make stops during the journey?',
    answer:
      'Yes, short stops for photos, scenic viewpoints, or refreshments are welcomed. For extended sightseeing stops or route detours, you can easily request them during booking.',
    column: 'right',
  },
  {
    id: 'faq-6',
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit and debit cards (Visa, MasterCard, American Express), PayPal, and cash payment to the driver. Official VAT receipts and invoices are emailed automatically.',
    column: 'right',
  },
]

const ADDITIONAL_FAQS: FAQData[] = [
  {
    id: 'faq-7',
    question: 'Are child safety seats available?',
    answer:
      'Yes, infant car seats, toddler seats, and booster seats are available free upon request during the booking process to ensure safety and comfort for your family.',
    column: 'left',
  },
  {
    id: 'faq-8',
    question: 'What is your cancellation policy?',
    answer:
      'You can cancel or reschedule free of charge up to 24 hours prior to your scheduled pickup time. Full refunds are processed promptly to your original payment method.',
    column: 'left',
  },
  {
    id: 'faq-9',
    question: 'How much luggage can I bring?',
    answer:
      'Our sedans accommodate up to 2-3 standard suitcases, while our Mercedes-Benz V-Class and Vito vans comfortably carry up to 8 passengers with 8-10 large suitcases and ski equipment.',
    column: 'right',
  },
  {
    id: 'faq-10',
    question: 'Can I modify my booking details later?',
    answer:
      'Yes, you can modify your pickup time, flight details, or drop-off location anytime before your journey by contacting our 24/7 customer support via WhatsApp or email.',
    column: 'right',
  },
]

type FAQItemProps = {
  item: FAQData
  isOpen: boolean
  onToggle: () => void
}

function FAQItem({ item, isOpen, onToggle }: FAQItemProps) {
  return (
    <motion.div
      layout
      className={`
        group overflow-hidden rounded-2xl border
        transition-all duration-300
        ${
          isOpen
            ? 'border-[#1A2428]/20 bg-[#F6F7F3] shadow-[0_8px_30px_rgba(0,0,0,0.05)]'
            : 'border-[#1A2428]/10 bg-white hover:border-[#1A2428]/20 hover:bg-[#FAFAF8]'
        }
      `}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${item.id}`}
        className="flex w-full cursor-pointer items-center justify-between gap-6 px-5 py-5 text-left sm:px-6"
      >
        <span
          className={`
            font-host text-[15px] font-medium leading-[1.45]
            transition-colors duration-200 sm:text-base
            ${
              isOpen
                ? 'text-[#1A2428]'
                : 'text-[#252B2D] group-hover:text-[#11191C]'
            }
          `}
        >
          {item.question}
        </span>

        <span
          className={`
            flex h-8 w-8 shrink-0 items-center justify-center
            rounded-full border
            transition-all duration-300
            ${
              isOpen
                ? 'rotate-180 border-[#1A2428] bg-[#1A2428] text-white'
                : 'border-[#1A2428]/15 bg-white text-[#1A2428] group-hover:border-[#1A2428]/30'
            }
          `}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3.25 5.25L7 9L10.75 5.25"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      <div
        id={`faq-answer-${item.id}`}
        className={`
          grid transition-[grid-template-rows,opacity]
          duration-300 ease-out
          ${
            isOpen
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          }
        `}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-6 sm:px-6">
            <div className="mb-4 h-px w-full bg-[#1A2428]/8" />

            <p className="max-w-[620px] font-host text-[14px] leading-[1.65] text-[#1A2428]/65 sm:text-[15px]">
              {item.answer}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default function FAQ() {
  // Only one FAQ can be open at any time.
  // null means that all FAQs are closed.
  const [openId, setOpenId] = useState<string | null>(null)

  const [showAll, setShowAll] = useState(false)

  const toggleItem = (id: string) => {
    setOpenId((currentId) => {
      // Clicking the currently open FAQ closes it.
      if (currentId === id) {
        return null
      }

      // Clicking another FAQ closes the previous one
      // and opens the new one.
      return id
    })
  }

  const allItems = showAll
    ? [...PRIMARY_FAQS, ...ADDITIONAL_FAQS]
    : PRIMARY_FAQS

  const leftItems = allItems.filter(
    (item) => item.column === 'left'
  )

  const rightItems = allItems.filter(
    (item) => item.column === 'right'
  )

  return (
    <section
      id="faq"
      className="mx-auto max-w-page px-4 py-20 sm:px-6 lg:px-[149px] lg:py-28"
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.08,
        }}
        variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.08,
            },
          },
        }}
      >
        {/* =========================
            SECTION HEADER
        ========================== */}
        <motion.div
          variants={{
            hidden: {
              opacity: 0,
              y: 18,
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.55,
                ease: 'easeOut',
              },
            },
          }}
          className="mb-10 max-w-[520px]"
        >
          <SectionHeading subtitle="Find answers to common questions about our transfers, day trips and booking process.">
            Frequently Asked Questions
          </SectionHeading>
        </motion.div>

        {/* =========================
            FAQ GRID
        ========================== */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-x-6 lg:gap-y-4">
          {/* LEFT COLUMN */}
          <div className="flex flex-col gap-4">
            {leftItems.map((item) => (
              <FAQItem
                key={item.id}
                item={item}
                isOpen={openId === item.id}
                onToggle={() => toggleItem(item.id)}
              />
            ))}
          </div>

          {/* RIGHT COLUMN */}
          <div className="flex flex-col gap-4">
            {rightItems.map((item) => (
              <FAQItem
                key={item.id}
                item={item}
                isOpen={openId === item.id}
                onToggle={() => toggleItem(item.id)}
              />
            ))}
          </div>
        </div>

        {/* =========================
            SHOW ALL BUTTON
        ========================== */}
        <motion.div
          variants={{
            hidden: {
              opacity: 0,
              y: 15,
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.5,
                ease: 'easeOut',
              },
            },
          }}
          className="mt-10 flex justify-center lg:justify-end"
        >
          <button
            type="button"
            onClick={() => {
              setShowAll((prev) => !prev)

              // Close any open FAQ when changing
              // between the primary and full FAQ list.
              setOpenId(null)
            }}
            className="
              group flex cursor-pointer items-center gap-3
              rounded-full
              border border-[#1A2428]/15
              bg-[#1A2428]
              px-6 py-3
              font-host text-sm font-medium text-white
              transition-all duration-300
              hover:-translate-y-0.5
              hover:bg-[#253136]
              hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)]
              active:translate-y-0
            "
          >
            <span>
              {showAll ? 'Show Fewer FAQs' : 'See All FAQs'}
            </span>

            <span
              className={`
                flex h-6 w-6 items-center justify-center
                rounded-full bg-white/10
                transition-transform duration-300
                ${showAll ? 'rotate-180' : ''}
              `}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M2.5 4.5L6 8L9.5 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
        </motion.div>
      </motion.div>
    </section>
  )
}